import { ExclamationCircleIcon } from "@heroicons/react/solid";
import useStableId from "@ui/hooks/unique-id";
import { SVGElement } from "@ui/types";
import classNames from "classnames";
import React, { ReactNode, useEffect, useImperativeHandle, useRef } from "react";

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  type?: string;
  name: string;
  label?: ReactNode;
  error?: string;
  leadingIcon?: SVGElement;
  trailingIcon?: SVGElement;
  labelHidden?: boolean;
  hint?: any;
  caption?: ReactNode;
  className?: string;
  autoComplete?: string;
}

export const Textarea = React.forwardRef(
  (props: TextareaProps, ref: React.Ref<HTMLTextAreaElement | null>) => {
    const id = useStableId(props);

    const internalRef = useRef<any>(null);

    useEffect(() => {
      if (props.autoFocus) {
        internalRef.current?.focus();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useImperativeHandle(ref, () => (internalRef.current ? internalRef.current : null));

    return (
      <div className={classNames("w-full", props.className)}>
        {(props.label || props.error) && (
          <div
            className={classNames("mb-1 flex  items-end", {
              "justify-between": props.label && !props.labelHidden,
              "justify-end": !props.label || props.labelHidden,
            })}
          >
            {props.label && (
              <label
                htmlFor={id}
                className={classNames("block text-sm font-medium text-foreground", {
                  "sr-only": props.labelHidden,
                })}
              >
                {props.label}
              </label>
            )}
            {props.error ? (
              <p className="dark:text-red-500 text-xs text-red-600">{props.error}</p>
            ) : props.hint ? (
              <span className="block text-xs text-muted-foreground">
                {typeof props.hint === "function" ? props.hint(props) : props.hint}
              </span>
            ) : null}
          </div>
        )}
        <div className="relative flex rounded-md shadow-sm">
          <div className="relative w-full min-w-0">
            <textarea
              id={id}
              ref={internalRef}
              name={props.name}
              value={props.value}
              autoComplete={props.autoComplete ?? "off"}
              required={props.required}
              onChange={props.onChange}
              onFocus={props.onFocus}
              onBlur={props.onBlur}
              onInput={props.onInput}
              disabled={props.disabled}
              className={classNames(
                "border focus:ring-slate-400 focus:border-slate-400 grow block w-full min-w-0 rounded-md sm:text-sm border-border bg-background text-foreground placeholder-muted-foreground",
                {
                  "rounded-l-none": !!props.children,
                  "pl-10": !!props.leadingIcon,
                },
                props.className,
              )}
              placeholder={props.placeholder}
              maxLength={props?.maxLength}
            />
            {props.error && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <ExclamationCircleIcon className="w-5 h-5 text-red-500" aria-hidden="true" />
              </div>
            )}
          </div>
        </div>
        {props.caption ? (
          <p className="mt-1.5 text-xs text-muted-foreground">{props.caption}</p>
        ) : null}
      </div>
    );
  },
);

Textarea.displayName = "Textarea";
